import { format, subDays } from 'date-fns';
import { ClassifierData, EntityClassifierValue } from 'types/classifiers';
import { t } from 'utils/getTranslations';

export const EFFORT_ACTIVITY_TYPE = 'EFFORT_ACTIVITY_TYPE';
const DEFAULT_STALE_AFTER_DAYS = 180;
const DEFAULT_ATTENTION_SEGMENTS = ['PREMIUM'];

/** The firm's "Last {type}" effort type, as the backend resolves it from the classifier's value parameters. */
export interface LastEffortType {
    code: string;
    /** The type's English name, else its code. */
    name: string;
    /** "Last {name}", the column and field label. */
    label: string;
    staleAfterDays: number;
    /** SEGMENT_TYPE codes whose clients need attention when the effort is missing or stale. */
    attentionSegments: string[];
}

// The backend takes a key only when it holds a JSON number / array, and its number as a whole day count.
const staleAfterDaysOf = (days: unknown) =>
    typeof days === 'number' && Number.isFinite(days) ? Math.round(days) : DEFAULT_STALE_AFTER_DAYS;
const attentionSegmentsOf = (segments: unknown) =>
    Array.isArray(segments) ? segments.map(String) : DEFAULT_ATTENTION_SEGMENTS;

const isActive = (value: EntityClassifierValue, now: Date) =>
    !value.deletedAt &&
    (!value.validFrom || new Date(value.validFrom) <= now) &&
    (!value.validUntil || new Date(value.validUntil) > now);

/**
 * The EFFORT_ACTIVITY_TYPE value flagged `lastEffortValue`, counted only while not deleted and inside its validity
 * window; of several, the lowest order. Null when the firm configures none, or before the classifiers load.
 */
export const lastEffortTypeOf = (
    classifier: ClassifierData | undefined,
    now: Date = new Date()
): LastEffortType | null => {
    const value = (classifier?.values ?? [])
        .filter((candidate) => candidate.parameters?.lastEffortValue === true && isActive(candidate, now))
        .sort((a, b) => (a.orderNum ?? Infinity) - (b.orderNum ?? Infinity))[0];
    if (!value) return null;
    const name = value.texts.find((text) => text.langCode.toLowerCase() === 'eng')?.value || value.code;
    return {
        code: value.code,
        name,
        label: t('contacts.last-effort-activity', { activityType: name }),
        staleAfterDays: staleAfterDaysOf(value.parameters.staleAfterDays),
        attentionSegments: attentionSegmentsOf(value.parameters.attentionSegments),
    };
};

/**
 * The portfolio's "needs attention" rule for the type: a client in one of its attention segments whose last such
 * effort is missing or older than its stale-after days.
 */
export const isLastEffortStale = (
    type: LastEffortType | null,
    segmentTypeCode: string | null | undefined,
    lastEffort: string | null | undefined,
    today: Date = new Date()
): boolean =>
    !!type &&
    !!segmentTypeCode &&
    type.attentionSegments.includes(segmentTypeCode) &&
    (!lastEffort || lastEffort < format(subDays(today, type.staleAfterDays), 'yyyy-MM-dd'));
